import type { MessageKey } from "./en";

/** German UI catalog. Every key in {@link en} must be present. */
export const de: Record<MessageKey, string> = {
  "app.title": "Rescript — Videos bearbeiten wie Text",
  "common.cancel": "Abbrechen",
  "common.close": "Schließen",
  "common.delete": "Löschen",
  "common.download": "Herunterladen",
  "common.import": "Importieren",
  "common.loading": "Wird geladen",
  "common.remove": "Entfernen",
  "common.restore": "Wiederherstellen",
  "common.retry": "Erneut versuchen",
  "common.searchOrCreate": "Suchen oder erstellen…",
  "common.settings": "Einstellungen",
  "common.system": "System",
  "common.tools": "Werkzeuge",
  "settings.appearance": "Darstellung",
  "settings.light": "Hell",
  "settings.dark": "Dunkel",
  "settings.interfaceLanguage": "Sprache der Oberfläche",
  "settings.privacy": "Datenschutz",
  "settings.helpImprove": "Hilf mit, die App zu verbessern",
  "settings.telemetryHelp": "Anonyme Statistiken zur Funktionsnutzung und Absturzberichte senden.",
  "settings.support": "Support / Feedback",
  "settings.reportIssue": "Problem melden",
  "settings.homepage": "Homepage",
  "settings.github": "GitHub",
  "settings.followX": "Auf X folgen",
  "model.transcriptSource": "Transkriptquelle",
  "model.language": "Sprache",
  "model.transcriptLanguage": "Transkriptsprache",
  "model.importTranscript": "Transkript importieren",
  "upload.recentProjects": "Letzte Projekte",
  "upload.removeRecent": "Aus letzten Projekten entfernen",
  "upload.dropPrefix": "Lege hier eine Video- oder Audiodatei ab, oder",
  "upload.browse": "durchsuchen",
  "upload.chooseTranscriptFirst": "Wähle oben im Menü ein Transkript und lege dann deine Medien ab",
  "upload.willUseTranscript": "{name} wird verwendet · MP4, WebM, MOV, MP3, WAV, …",
  "upload.mediaFormats": "MP4, WebM, MOV, MP3, WAV, M4A, …",
  "upload.gettingReady": "Alles wird vorbereitet",
  "upload.gettingReadyHelp": "Die Medien-Engine wird eingerichtet. Das passiert nur einmal.",
  "upload.unsupported": "Dieser Browser kann den Editor nicht ausführen",
  "upload.unsupportedHelp":
    "Die Bearbeitung benötigt SharedArrayBuffer, dafür muss die Seite Cross-Origin-isoliert sein. Probiere eine aktuelle Version von Chrome, Edge, Safari oder Firefox über HTTPS.",
  "upload.unsupportedSimd": "Dieses Gerät kann den Editor nicht ausführen",
  "upload.unsupportedSimdHelp": "Zum Bearbeiten wird WebAssembly SIMD benötigt, das dieser Prozessor nicht meldet. Aktualisiere auf einem Windows-on-Arm-PC auf die neueste Rescript-Version — aktuelle Installer enthalten einen nativen Arm64-Build, der nicht emuliert läuft.",
  "upload.transcribeTitle": "Transkribieren",
  "upload.transcribeText": "Whisper lokal nutzen oder SRT / VTT importieren.",
  "upload.editTitle": "Bearbeiten",
  "upload.editText": "Wörter auswählen und Delete drücken, um zu schneiden.",
  "upload.exportTitle": "Exportieren",
  "upload.exportText": "Den finalen Schnitt als MP4 oder M4A rendern.",
  "editor.chooseTranscript": "Wähle zuerst eine Transkriptdatei im Quellenmenü.",
  "editor.chooseMedia": "Bitte wähle eine Video- oder Audiodatei.",
  "editor.undo": "Rückgängig (⌘Z)",
  "editor.redo": "Wiederholen (⇧⌘Z)",
  "editor.export": "Exportieren",
  "topbar.startOver": "Neu anfangen",
  "transcript.replace": "Transkript aus SRT, VTT oder JSON ersetzen",
  "transcript.header": "Transkript",
  "transcript.wordDeleted": "{count} Wort",
  "transcript.wordsDeleted": "{count} Wörter",
  "transcript.replaceConfirm": "Aktuelles Transkript durch diese Datei ersetzen?",
  "transcript.invalidFile": "Wähle eine SRT-, VTT- oder JSON-Transkriptdatei.",
  "transcript.noSpeech": "Keine Sprache erkannt. Stelle sicher, dass diese Datei Audio enthält, oder importiere ein Transkript.",
  "transcript.cut": "Schneiden",
  "transcript.follow": "Abspielposition folgen",
  "transcript.hideDeleted": "Gelöschte Wörter ausblenden",
  "transcript.showDeleted": "Gelöschte Wörter anzeigen",
  "transcript.correct": "Korrigieren",
  "transcript.scrollWithPlayhead": "Mit der Abspielposition scrollen",
  "transcript.joinSplit": "Clip geteilt — klicken, um diese Clips zu verbinden",
  "transcript.joinClips": "Clips verbinden",
  "transcript.hesitation": "Zögern erkannt (nicht transkribiert) — mit Füllwörter entfernen schneiden",
  "transcript.textLabel": "Transkripttext",
  "transcript.textHint": "Pfeiltasten bewegen sich durch die Wörter und versetzen den Abspielkopf. Umschalt mit Pfeiltasten wählt aus. Rücktaste oder Entf schneidet Wörter, Eingabe korrigiert sie, Leertaste spielt ab oder pausiert, und Rückgängig holt Schnitte zurück.",
  "transcript.textHesitation": "[Zögern]",
  "transcript.textCut": "Geschnitten: {text}",
  "transcript.textNothingToCut": "Nichts zu schneiden",
  "transcript.textUndo": "Rückgängig",
  "transcript.textRedo": "Wiederholen",
  "tools.bulk": "Transkript gesammelt bereinigen",
  "tools.removeFillers": "Füllwörter entfernen",
  "tools.removeFillersTitle": "Füllwörter (\"ähm\", \"äh\", \"...\", …) aus dem Video schneiden",
  "tools.restoreFillers": "Füllwörter wiederherstellen",
  "tools.restoreFillersTitle": "Alle geschnittenen Füllwörter zurückholen",
  "tools.removeSilences": "Stille entfernen",
  "tools.removeSilencesTitle": "Pausen und Stille (≥{seconds} s) aus dem Video schneiden",
  "tools.restoreSilences": "Stille wiederherstellen",
  "tools.restoreSilencesTitle": "Alle geschnittenen Pausen zurückholen",
  "timeline.removed": "{trimmed} entfernt — ursprüngliche Länge {duration}",
  "timeline.back5": "5 s zurück",
  "timeline.playPause": "Wiedergabe / Pause (Leertaste)",
  "timeline.forward5": "5 s vor",
  "timeline.split": "Teilen",
  "timeline.splitTitle": "Clip an der Abspielposition teilen (S)",
  "timeline.splitDisabled": "Bewege die Abspielposition auf einen behaltenen Bereich, um zu teilen",
  "timeline.delete": "Löschen",
  "timeline.deleteTitle": "Ausgewählten Clip löschen (Delete)",
  "timeline.deleteDisabled": "Wähle einen Clip in der Timeline aus, um ihn zu löschen",
  "timeline.restore": "Wiederherstellen",
  "timeline.restoreTitle": "Ausgewählten Schnitt wiederherstellen (Delete)",
  "timeline.restoreDisabled": "Wähle einen Schnitt oder Stille-Abschnitt in der Timeline aus, um ihn wiederherzustellen",
  "timeline.zoomOut": "Verkleinern",
  "timeline.fit": "An Fenster anpassen",
  "timeline.zoomIn": "Vergrößern — Wortkanten ziehen, um Timing zu verfeinern",
  "timeline.joinClips": "Diese Clips verbinden (Teilung entfernen)",
  "timeline.trimStart": "Clip-Anfang trimmen",
  "timeline.trimEnd": "Clip-Ende trimmen",
  "timeline.hesitationAdjust": "… Zögern erkannt — Kanten ziehen zum Anpassen oder mit Füllwörter entfernen schneiden",
  "timeline.hesitationCut": "… Zögern erkannt — mit Füllwörter entfernen schneiden",
  "timeline.dragTiming": "{word} — Kanten ziehen, um Timing anzupassen",
  "timeline.scrollZoom": "Scrollen zum Vergrößern / Verkleinern",
  "speaker.moveStart": "Ziehen, um zu verschieben, wo dieser Sprecher beginnt",
  "speaker.moveLabel": "Sprecherlabel verschieben",
  "speaker.options": "Sprecheroptionen",
  "speaker.change": "Sprecher ändern",
  "speaker.rename": "Sprecher umbenennen",
  "speaker.renameAction": "Umbenennen",
  "speaker.replace": "Im Projekt ersetzen durch…",
  "speaker.removeProject": "Aus Projekt entfernen",
  "speaker.newName": "Neuer Name",
  "speaker.find": "Sprecher suchen…",
  "speaker.noMatches": "Keine passenden Sprecher",
  "speaker.renameTo": "{current} in {next} umbenennen",
  "speaker.button": "Sprecher",
  "speaker.search": "Suchen oder erstellen…",
  "speaker.defaultName": "Sprecher {number}",
  "speaker.create": "\"{name}\" erstellen",
  "export.title": "Exportieren",
  "export.type": "Exporttyp",
  "export.video": "Video",
  "export.audio": "Audio",
  "export.transcript": "Transkript",
  "export.subtitles": "Untertitel",
  "export.videoUnavailable": "Videoexport ist für reine Audioprojekte nicht verfügbar",
  "export.noAudio": "Diese Datei hat keine Audiospur",
  "export.noWordsFirst": "Zuerst transkribieren oder ein Transkript importieren",
  "export.format": "Format",
  "export.resolution": "Auflösung",
  "export.original": "Original",
  "export.plainText": "TXT",
  "export.markdown": "Markdown",
  "export.statOriginal": "Original",
  "export.statCuts": "Schnitte",
  "export.statEdited": "Bearbeitet",
  "export.includeTimestamps": "Zeitstempel einbeziehen",
  "export.speakerLabels": "Sprechernamen einbeziehen",
  "export.shortCues": "Kurze Untertitel (≤5 s)",
  "export.transcriptHelp": "Text mit Sprecherlabels und entfernten Schnitten.",
  "export.transcriptHelpTimestamps":
    "Text mit Sprecherlabels und entfernten Schnitten. Jeder Abschnitt beginnt mit der Zeit auf der bearbeiteten Timeline.",
  "export.transcriptHelpPdf":
    "PDF nutzt eine lateinische Standardschrift — für volles Unicode (CJK usw.) besser DOCX verwenden.",
  "export.subtitlesHelp":
    "SRT und VTT verwenden die bearbeitete Timeline (Schnitte angewendet). JSON behält die vollständige Wortliste für den erneuten Import.",
  "export.subtitlesHelpShortCues":
    "SRT und VTT verwenden die bearbeitete Timeline (Schnitte angewendet), aufgeteilt in kurze satzweise Untertitel (≤5 s). JSON behält die vollständige Wortliste für den erneuten Import.",
  "export.encodingHelp": "Neucodierung mit ffmpeg.wasm — längere Dateien dauern eine Weile.",
  "export.reexport": "Mit neuesten Änderungen erneut exportieren",
  "export.rendering": "Rendering in deinem Browser…",
  "export.downloadFile": "{name} herunterladen",
  "export.saveFile": "{name} sichern…",
  "export.saving": "Wird gesichert…",
  "export.saveDialogTitle": "Export sichern",
  "export.savedAs": "Gesichert als {name}",
  "export.showInFinder": "Im Finder zeigen",
  "export.showInExplorer": "Im Explorer anzeigen",
  "export.showInFolder": "Im Ordner anzeigen",
  "export.renderingNative": "Rendering…",
  "export.encodingHelpNative": "Neucodierung mit ffmpeg — längere Dateien dauern eine Weile.",
  "export.downloadFormat": ".{format} herunterladen",
  "export.exportFormat": "{format} exportieren",
  "export.timeline": "Timeline",
  "export.timelineTitle": "NLE-/DAW-Sequenz exportieren (Resolve, Premiere, Final Cut, Pro Tools, Reaper, Blender)",
  "export.nle": "Format",
  "export.frameRate": "Bildrate",
  "export.timelineHelp":
    "Die Sequenz verweist auf das Originalmedium per Dateiname — nach dem Import im NLE neu verknüpfen.",
  "export.timelineHelpAaf": "Pro Tools / Logic AAF (nur Metadaten).",
  "export.timelineHelpFcpx": "Final Cut Pro FCPXML.",
  "export.timelineHelpPremiere": "Adobe Premiere Pro XML (xmeml).",
  "export.timelineHelpResolve": "DaVinci Resolve XML (xmeml).",
  "export.timelineHelpReaper": "Reaper-Projekt (.rpp).",
  "export.timelineHelpSamplitude":
    "Samplitude-EDL (.edl) — öffnet sich auch direkt in Reaper.",
  "export.timelineHelpBlender":
    "Blender-Videoschnittprojekt (.blend, Blender 5.2+) — im selben Ordner wie die Originaldatei speichern.",
  "export.aafOverCap":
    "Dieser Schnitt hat {count} Clips; AAF unterstützt bis zu {max}. Nutze Resolve, Premiere, Final Cut, Reaper oder Samplitude-EDL.",
  "export.preparing": "Wird vorbereitet…",
  "import.reading": "Transkript wird gelesen…",
  "import.importing": "Import läuft…",
  "import.failed": "Import fehlgeschlagen",
  "import.readingFile": "Datei wird gelesen…",
  "import.chooseFileShort": "Datei wählen…",
  "import.chooseFile": "SRT-, VTT- oder JSON-Transkript wählen",
  "import.selected": "{name} ausgewählt",
  "banner.faster": "Schnellere Transkription und Exporte?",
  "banner.getDesktop": "Rescript Desktop-App holen",
  "banner.downloadFor": "Für {platform} herunterladen",
  "banner.dismiss": "Ausblenden",
  "social.githubRepo": "GitHub-Repository",
  "social.discordServer": "Discord-Server",
  "social.xProfile": "X-Profil",
  "globalError.title": "Rescript — etwas ist schiefgelaufen",
  "globalError.heading": "Etwas ist schiefgelaufen",
  "globalError.body":
    "Im Editor ist ein unerwarteter Fehler aufgetreten. Deine gespeicherten Projekte sind noch auf diesem Gerät — nach dem Neuladen sollten sie wieder da sein.",
  "progress.loadingMedia": "Medien werden geladen…",
  "progress.loadingMediaEngine": "Medien-Engine wird geladen…",
  "progress.extractingAudio": "Audio wird extrahiert…",
  "progress.preparingMedia": "Medien werden vorbereitet…",
  "progress.loadingSpeechModel": "Sprachmodell wird geladen…",
  "progress.loadingSpeechCache": "Sprachmodell wird aus Cache geladen…",
  "progress.downloadingSpeech": "Sprachmodell wird heruntergeladen…",
  "progress.gpuFallback": "GPU unterbrochen — Fortsetzung auf CPU…",
  "progress.detectingSpeech": "Sprache wird erkannt…",
  "progress.transcribing": "Transkription läuft…",
  "progress.loadingAlignCache": "Alignment-Modell wird aus Cache geladen…",
  "progress.downloadingAlign": "Alignment-Modell wird heruntergeladen…",
  "progress.aligning": "Wörter werden ausgerichtet…",
  "progress.speakers": "Sprecher werden erkannt…",
  "error.selectModel": "Wähle ein Sprachmodell zum Transkribieren aus.",
  "error.workerCrashed": "Transkriptions-Worker ist abgestürzt.",
  "error.mediaEngineNetwork": "Die Medien-Engine konnte nicht geladen werden — die Verbindung wurde unterbrochen. Prüfe deine Internetverbindung und versuche es erneut.",
  "error.mediaEngineStalled": "Die Medien-Engine hat nicht mehr reagiert und wurde neu gestartet. Schließe andere Apps und Tabs, um Speicher freizugeben, und versuche es erneut.",
  "error.mediaEngineNotReady": "Die Medien-Engine ist noch nicht bereit — lade die Seite neu und versuche es erneut.",
  "error.simdUnsupported": "Dieser Prozessor unterstützt kein WebAssembly SIMD, das die Medien-Engine benötigt. Aktualisiere auf einem Windows-on-Arm-PC auf die neueste Rescript-Version — aktuelle Installer enthalten einen nativen Arm64-Build, der nicht emuliert läuft.",
  "error.processFile": "Diese Datei konnte nicht verarbeitet werden.",
  "error.extractAudio": "Audio konnte aus dieser Datei nicht extrahiert werden.",
  "error.nothingToExport": "Alles wurde gelöscht — nichts zu exportieren.",
  "error.videoExport": "Export beim Rendern des Videos fehlgeschlagen.",
  "error.audioExport": "Export beim Rendern des Audios fehlgeschlagen.",
  "error.exportSave": "Die exportierte Datei konnte nicht gesichert werden.",
  "error.diskFull": "Nicht genügend freier Speicherplatz. Gib Speicher frei und versuche es erneut.",
  "error.export": "Export fehlgeschlagen.",
  "error.timelineExport": "Timeline-Export fehlgeschlagen.",
  "error.timelineEmpty": "Nichts auf die Timeline zu setzen.",
  "error.aafTemplate": "AAF-Exportvorlage konnte nicht geladen werden.",
  "error.emptyTranscript": "Diese Transkriptdatei ist leer.",
  "error.noTimedWords": "Keine Wörter mit Zeitangaben in diesem Transkript gefunden.",
  "error.parseJson": "Dieses JSON-Transkript konnte nicht geparst werden.",
  "error.jsonShape": 'JSON muss ein Wortarray oder { "words": [...] } sein.',
  "error.noWords": "Keine Wörter zum Exportieren.",
  "error.projectMissing": "Dieses Projekt ist nicht mehr gespeichert.",
  "error.openProject": "Dieses Projekt konnte nicht geöffnet werden.",
  "error.removeProject": "Dieses Projekt konnte nicht entfernt werden.",
  "error.readTranscript": "Dieses Transkript konnte nicht gelesen werden.",
  "error.clearRecent": "Letzte Projekte konnten nicht gelöscht werden.",
  "error.modelDownload":
    "Der Download des Sprachmodells konnte nicht abgeschlossen werden — die Verbindung wurde unterbrochen. Prüfe deine Internetverbindung und versuche es erneut; bereits heruntergeladene Teile bleiben erhalten.",
  "error.gpuReset": "Die Transkription wurde unterbrochen, als die GPU zurückgesetzt wurde (oft nach dem Sperren des Bildschirms). Bitte versuche es erneut.",
  "confirm.clearRecent": "Alle letzten Projekte entfernen? Ihre gespeicherten Bearbeitungen werden gelöscht.",
};
